import { CommonModule } from '@angular/common';
import { Component, ElementRef, OnInit, ViewChild, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Router } from '@angular/router';
import { SessionStorageService } from '../../../core/services/session-storage/session-storage.service';
import { PiasLayoutComponent } from '../../../shared/components/pias-layout/pias-layout.component';
import { SnackbarService } from '../../../shared/services/snackbar/snackbar.service';
import { ConfirmPopupComponent } from '../../agent-onboarding/new-rep-application/confirm-popup/confirm-popup.component';
import { AdStatusBadgeComponent } from '../marketing-review-components/ad-status-badge/ad-status-badge.component';
import { PinDraft, PinViewerComponent, pinViewerSupports } from '../marketing-review-components/pin-viewer/pin-viewer.component';
import { AD_ACCEPT_ATTRIBUTE, AdCommentPin, AdFileKind, AdReview, adReviewComments, AdReviewCriterion, AdStatus, CLOSED_REVIEW_STATUSES, documents, MarketingAd, MarketingAdDetails, ReviewDecision } from '../marketing-review-interfaces/marketing-review.interface';
import { MarketingReviewService } from '../marketing-review-service/marketing-review.service';
import { HttpStatusCode } from 'axios';
import { ReviewAdModel } from './review-ad-models/review-ad.model';
import { ReviewAdService } from './review-ad-service/review-ad.service';

type ThreadEntry = | { kind: 'comment'; key: string; at: string; comment: adReviewComments } | { kind: 'review'; key: string; at: string; review: AdReview };

/** One entry in the media viewer's segmented control. */
interface MediaTab {
    label: string;
    fileName: string;
    url: any;
    kind: AdFileKind;
    meta: string;
    icon: string;
    tone: string;
    attachmentId: number | null;
    pinnable: boolean;
}

/** One file in the replace-material list, resolved once when the list changes. */
interface ReplacementFile {
    attachment: documents;
    icon: string;
    tone: string;
    // meta: string;
}

/**
 * Review page — the creative in a zoomable viewer on the left, the comment thread on the right,
 * with the decision actions in the header.
 *
 * Per the design, the reviewer records a decision with comments only; there is no per-criterion
 * checklist. `AdReview.criteria` is therefore saved empty on new reviews. Reviews raised before
 * this change (and the seeded mock data) still carry criteria, and any marked `No` are shown under
 * their comment card by `concerns()` — so historical detail is not lost.
 *
 * Reviews accumulate rather than overwrite, so the thread between a branch and the marketing team
 * is preserved on the ad. Opens read-only once a decision is final.
 */
@Component({
    selector: 'app-review-ad',
    imports: [
        CommonModule,
        FormsModule,
        MatButtonModule,
        MatIconModule,
        MatTooltipModule,
        PiasLayoutComponent,
        AdStatusBadgeComponent,
        PinViewerComponent
    ],
    templateUrl: './review-ad.component.html',
    styleUrl: './review-ad.component.scss'
})
export class ReviewAdComponent implements OnInit {

    private router = inject(Router);
    private matDialog = inject(MatDialog);
    private snackBarService = inject(SnackbarService);
    private sessionStorageService = inject(SessionStorageService);
    private marketingReviewService = inject(MarketingReviewService);
    private adMarketingReviewService = inject(ReviewAdService);

    @ViewChild(PinViewerComponent) private pinViewer?: PinViewerComponent;
    @ViewChild('composerInput') private composerInput?: ElementRef<HTMLTextAreaElement>;

    protected readonly setSprinner = signal(false);

    protected notFound: boolean = false;
    protected adDetails!: MarketingAdDetails;
    protected adComments!: adReviewComments[];

    protected overallComments: string = '';
    protected commentsTouched: boolean = false;

    /** Id of the comment being edited inline, `null` when nothing is. */
    protected editingCommentId: number | null = null;
    protected editingText: string = '';

    /** Media viewer state. */
    protected mediaTabs: MediaTab[] = [];
    protected activeMedia: number = 0;
    protected zoom: number = 100;

    private readonly ZOOM_MIN: number = 50;
    private readonly ZOOM_MAX: number = 200;
    private readonly ZOOM_STEP: number = 25;

    /** Replace-material state — see `canReplaceMaterial`. */
    protected readonly ACCEPTED_FILE_TYPES: string = AD_ACCEPT_ATTRIBUTE;
    protected replacingMaterial: boolean = false;
    /** The working set while replacing; the ad itself is untouched until resubmit. */
    protected replacementFiles: ReplacementFile[] = [];

    /** Pin state — see the Pins section. */
    protected draftPin: AdCommentPin | null = null;
    protected activePinId: number | null = null;
    /** Pinned comments on the open tab, kept as a field so the viewer is not handed a new array each pass. */
    protected mediaPins: adReviewComments[] = [];
    /** Reply box text, per comment id. */
    protected replyText: Record<number, string> = {};
    data = history.state;
    applicationId!: number;
    ngOnInit(): void {
        this.applicationId = this.data.applicationId;
        this.loadAd();
    }

    /**
     * Prefer the ad handed over in router state; fall back to the `id` query param so a hard
     * refresh on this URL still resolves.
     */
    private async loadAd(): Promise<void> {
        this.setSprinner.set(true);
        const responseData = await this.adMarketingReviewService.getAdById(this.applicationId)
            .then((response) => {
                return JSON.parse(response);
            })
            .catch((error) => {
                // console.error(error);
                return error;
            });
        this.setSprinner.set(false);
        if (responseData.status === HttpStatusCode.Ok) {
            this.adDetails = responseData.data.responsedata.MarketingReviewApplication;
            console.log(this.adDetails);
            this.rebuildThread();
            this.buildMediaTabs();

        } else {
            if (responseData.code === HttpStatusCode.BadRequest) {
                // Display notification on API failure.
                this.snackBarService.openFailureSnackBar("We couldn’t process your request. Please try again");
            }
            if (responseData.code === HttpStatusCode.Unauthorized) {
                this.snackBarService.openFailureSnackBar("Session timed out. Try logging in again!");
                this.router.navigate(['/login']);
            }
        }
    }


    // ── Media viewer ────────────────────────────────────────────────────────────

    /**
     * One tab per attachment, plus the hero creative when it is not one of them already — an ad
     * whose key visual *is* an attachment would otherwise open on a duplicate tab.
     */
    private async buildMediaTabs(): Promise<void> {
        const tabs: MediaTab[] = [];
        // const attachedUrls: number[] = this.adDetails.documents.map((attachment) => attachment.documentId);

        // if (this.ad.previewImageUrl && !attachedUrls.includes(this.ad.previewImageUrl)) {
        // tabs.push({
        //     label: 'Key visual',
        //     fileName: this.adMediaFiles[0].documentTitle,
        //     url: url,
        //     kind: 'image',
        //     meta: 'Key visual',
        //     icon: 'image',
        //     tone: 'image',
        //     attachmentId: null,
        //     pinnable: true
        // });
        // }

        this.adDetails.documents.forEach((attachment) => {
            const kind: AdFileKind = this.adMarketingReviewService.fileKindOf(attachment.documentName);
            const format: string = this.adMarketingReviewService.formatLabelOf(attachment.documentName);

            tabs.push({
                label: format,
                fileName: attachment.documentName,
                url: attachment.url,
                kind,
                meta: '',
                icon: this.adMarketingReviewService.iconOf(attachment.documentName),
                tone: this.adMarketingReviewService.toneOf(attachment.documentName),
                attachmentId: attachment.documentId,
                pinnable: pinViewerSupports(attachment.documentName, kind)
            });
        });

        this.mediaTabs = tabs;
        this.activeMedia = 0;
        this.refreshMediaPins();
    }

    protected get media(): MediaTab | null {
        return this.mediaTabs[this.activeMedia] ?? null;
    }

    protected selectMedia(index: number): void {
        this.activeMedia = index;
        this.zoom = 100;
        this.refreshMediaPins();
    }

    protected zoomIn(): void {
        this.zoom = Math.min(this.ZOOM_MAX, this.zoom + this.ZOOM_STEP);
    }

    protected zoomOut(): void {
        this.zoom = Math.max(this.ZOOM_MIN, this.zoom - this.ZOOM_STEP);
    }

    protected get canZoomIn(): boolean {
        return this.zoom < this.ZOOM_MAX;
    }

    protected get canZoomOut(): boolean {
        return this.zoom > this.ZOOM_MIN;
    }

    // ── View state ──────────────────────────────────────────────────────────────

    /** A finalised decision cannot be re-reviewed from here. */
    protected get isReadOnly(): boolean {
        return !!this.adDetails && CLOSED_REVIEW_STATUSES.includes(this.adDetails.status);
    }

    /** Flags the composer only when a decision was attempted with nothing to go on. */
    protected get commentsInvalid(): boolean {
        return this.commentsTouched && !this.overallComments.trim() && !this.threadCount;
    }

    // /** Header meta line: reference, submitter, branches and channel. */
    // protected get metaLine(): string {
    //     if (!this.ad) {
    //         return '';
    //     }

    //     const parts: string[] = [
    //         this.ad.referenceNo,
    //         `${this.ad.createdBy}${this.ad.createdByRepCode ? ` (${this.ad.createdByRepCode})` : ''}`,
    //         this.ad.branches.join(', '),
    //         this.ad.channel
    //     ];

    //     return parts.filter(Boolean).join(' · ');
    // }

    // ── Thread ──────────────────────────────────────────────────────────────────

    /**
     * Comments and decisions in one list, oldest first, so the conversation reads in order.
     *
     * Rebuilt whenever the ad changes rather than exposed as a getter — a getter would hand the
     * `@for` a freshly sorted array on every change-detection pass.
     */
    protected thread: ThreadEntry[] = [];

    private async rebuildThread(): Promise<void> {
        // if (!this.ad) {
        //     this.thread = [];
        //     // return;
        // }

        const responseData = await this.adMarketingReviewService.getAllCommentsById(this.applicationId)
            .then((response) => {
                return JSON.parse(response);
            })
            .catch((error) => {
                // console.error(error);
                return error;
            });
        this.setSprinner.set(false);
        if (responseData.status === HttpStatusCode.Ok) {
            this.adComments = responseData.data.responsedata;
            console.log(this.adComments);
            const entries: ThreadEntry[] = [
                ...this.adComments.map((comment: adReviewComments): ThreadEntry => ({
                    kind: 'comment',
                    key: `comment-${comment.adId}`,
                    at: comment.createdDate,
                    comment
                })),
                // ...this.ad.reviews.map((review): ThreadEntry => ({
                //     kind: 'review',
                //     key: `review-${review.id}`,
                //     at: review.reviewedOn,
                //     review
                // }))
            ];
            this.thread = entries.sort((a, b) => new Date(a.at).getTime() - new Date(b.at).getTime());
            this.buildMediaTabs();
            this.refreshMediaPins();

        } else {
            if (responseData.code === HttpStatusCode.BadRequest) {
                // Display notification on API failure.
                this.snackBarService.openFailureSnackBar("We couldn’t process your request. Please try again");
            }
            if (responseData.code === HttpStatusCode.Unauthorized) {
                this.snackBarService.openFailureSnackBar("Session timed out. Try logging in again!");
                this.router.navigate(['/login']);
            }
        }
    }

    protected get threadCount(): number {
        return this.thread.length;
    }

    /**
     * Edit and delete are offered on your own comments only, and only while the review is still
     * open — once a decision is final the thread it was based on stays as it was.
     */
    protected canModify(comment: adReviewComments): boolean {
        return !this.isReadOnly && comment.commentedBy === this.currentUserName;
    }

    protected startEdit(comment: adReviewComments): void {
        this.editingCommentId = comment.adId;
        this.editingText = comment.text;
    }

    protected cancelEdit(): void {
        this.editingCommentId = null;
        this.editingText = '';
    }

    protected async saveEdit(comment: adReviewComments): Promise<void> {
        if (!this.adDetails || !this.editingText.trim()) {
            this.snackBarService.openWarningSnackBar('A comment cannot be left empty.');
            return;
        }
        let updateReviewComments = new ReviewAdModel();
        updateReviewComments.setCommentId(comment.adId);
        updateReviewComments.setCommentedBy(this.sessionStorageService.getCurrentUserName());
        updateReviewComments.setPiasRepCode(this.sessionStorageService.getCurrentRepCode());
        updateReviewComments.setDescription(this.editingText.toString());


        this.setSprinner.set(true);
        const responseData = await this.adMarketingReviewService.updateComment(this.applicationId, updateReviewComments)
            .then((response) => {
                return JSON.parse(response);
            })
            .catch((error) => {
                // console.error(error);
                return error;
            });

        if (responseData.status === HttpStatusCode.Ok) {
            this.snackBarService.openSuccessSnackBar('Comment Updated.');
            this.cancelEdit();
            this.rebuildThread();
        } else {
            if (responseData.code === HttpStatusCode.BadRequest) {
                // Display notification on API failure.
                this.snackBarService.openFailureSnackBar("We couldn’t process your request. Please try again");
            }
            if (responseData.code === HttpStatusCode.Unauthorized) {
                this.snackBarService.openFailureSnackBar("Session timed out. Try logging in again!");
                this.router.navigate(['/login']);
            }
        }
        // await this.marketingReviewService.updateComment(this.ad.id, comment.id, this.editingText)
        //     .then((updated) => {
        //         this.setAd(updated);
        //         this.cancelEdit();
        //     })
        //     .catch((error) => {
        //         console.error('Failed to update the comment:', error);
        //         this.snackBarService.openFailureSnackBar('We couldn’t update that comment. Please try again');
        //     });
        this.setSprinner.set(false);
    }

    protected deleteComment(comment: adReviewComments): void {
        this.matDialog
            .open(ConfirmPopupComponent, {
                width: '420px',
                data: {
                    message: 'Delete this comment?',
                    message1: 'It will be removed from the thread. The audit log keeps a record.',
                    hidecancel: false
                }
            })
            .afterClosed()
            .subscribe((result) => {
                if (result?.data) {
                    this.removeComment(comment);
                }
            });
    }

    private async removeComment(comment: adReviewComments): Promise<void> {
        if (!this.adDetails) {
            return;
        }

        this.setSprinner.set(true);
        const responseData = await this.adMarketingReviewService.deleteComment(this.applicationId, comment.adId)
            .then((response) => {
                return JSON.parse(response);
            })
            .catch((error) => {
                // console.error(error);
                return error;
            });

        if (responseData.status === HttpStatusCode.Ok) {
            this.snackBarService.openSuccessSnackBar('Comment deleted.');
            this.rebuildThread();
        } else {
            if (responseData.code === HttpStatusCode.BadRequest) {
                // Display notification on API failure.
                this.snackBarService.openFailureSnackBar("We couldn’t process your request. Please try again");
            }
            if (responseData.code === HttpStatusCode.Unauthorized) {
                this.snackBarService.openFailureSnackBar("Session timed out. Try logging in again!");
                this.router.navigate(['/login']);
            }
        }
        // await this.marketingReviewService.deleteComment(this.ad.id, comment.id)
        //     .then((updated) => {
        //         this.setAd(updated);
        //         this.snackBarService.openSuccessSnackBar('Comment deleted.');
        //     })
        //     .catch((error) => {
        //         console.error('Failed to delete the comment:', error);
        //         this.snackBarService.openFailureSnackBar('We couldn’t delete that comment. Please try again');
        //     });
        this.setSprinner.set(false);
    }

    /**
     * Posts the composer's text as a comment, leaving the ad's status untouched. With a pin
     * waiting in the composer, the comment is pinned to it.
     */
    protected async postComment(): Promise<void> {
        this.commentsTouched = true;

        if (!this.adDetails || !this.overallComments.trim()) {
            this.snackBarService.openWarningSnackBar('Write a comment before sharing it.');
            return;
        }

        const pin: AdCommentPin | null = this.draftPin;
        // const request: Promise<MarketingAd> = pin
        //     ? this.marketingReviewService.addPinComment(this.ad.id, this.overallComments, pin)
        //     : this.marketingReviewService.addComment(this.ad.id, this.overallComments);

        let newReviewComments = new ReviewAdModel();
        newReviewComments.setCommentedBy(this.sessionStorageService.getCurrentUserName());
        newReviewComments.setPiasRepCode(this.sessionStorageService.getCurrentRepCode());
        newReviewComments.setDescription(this.overallComments.toString());
        pin ? newReviewComments.setPin(pin) : '';

        this.setSprinner.set(true);
        const responseData = await this.adMarketingReviewService.addComment(this.applicationId, newReviewComments)
            .then((response) => {
                return JSON.parse(response);
            })
            .catch((error) => {
                // console.error(error);
                return error;
            });

        if (responseData.status === HttpStatusCode.Ok) {
            this.snackBarService.openSuccessSnackBar(pin ? 'Pinned comment shared with the branch.' : 'Comment shared with the branch.');
            this.overallComments = '';
            this.commentsTouched = false;
            this.draftPin = null;
            this.rebuildThread();
            this.refreshMediaPins();
        } else {
            if (responseData.code === HttpStatusCode.BadRequest) {
                // Display notification on API failure.
                this.snackBarService.openFailureSnackBar("We couldn’t process your request. Please try again");
            }
            if (responseData.code === HttpStatusCode.Unauthorized) {
                this.snackBarService.openFailureSnackBar("Session timed out. Try logging in again!");
                this.router.navigate(['/login']);
            }
        }
        // await request
        //     .then((updated) => {
        //         this.setAd(updated);
        //         
        //        
        //     })
        //     .catch((error) => {
        //         console.error('Failed to add the comment:', error);
        //         this.snackBarService.openFailureSnackBar('We couldn’t share that comment. Please try again');
        //     });
        this.setSprinner.set(false);
    }

    protected get currentUserName(): string {
        return this.sessionStorageService.getCurrentUserPersonName() || 'Current User';
    }

    /** Criteria marked `No` on a past review, shown under its comment card. */
    protected concerns(review: AdReview): AdReviewCriterion[] {
        return review.criteria.filter((criterion) => criterion.response === 'No');
    }

    // ── Pins ────────────────────────────────────────────────────────────────────

    private refreshMediaPins(): void {
        const documentId: number | null | undefined = this.media?.attachmentId;
        this.mediaPins = !this.adComments || documentId === undefined
            ? []
            : this.adComments.filter((comment) => comment.pin?.documentId === documentId);
    }

    protected get pinTotal(): number {
        return this.adComments?.filter((comment) => comment.pin).length ?? 0;
    }

    protected get pinOpen(): number {
        return this.adComments?.filter((comment) => comment.pin && comment.status !== 'Resolved').length ?? 0;
    }

    /** The draft pin, but only on the tab it was made on. */
    protected get mediaDraft(): PinDraft | null {
        return this.draftPin && this.draftPin.documentId === this.media?.attachmentId ? this.draftPin : null;
    }

    /** The viewer placed a pin — hand it to the composer, which posts it with the comment. */
    protected onPinDraft(draft: PinDraft): void {
        if (!this.media) {
            return;
        }
        this.draftPin = { ...draft, documentId: this.media.attachmentId };
        this.activePinId = null;
        setTimeout(() => this.composerInput?.nativeElement.focus());
    }

    protected discardDraftPin(): void {
        this.draftPin = null;
    }

    /** A pin was clicked on the material — highlight its card in the thread. */
    protected onPinSelect(commentId: number): void {
        this.activePinId = commentId;
        document.getElementById(`thread-comment-${commentId}`)
            ?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }

    /** A card was clicked — open the file its pin is on and scroll the pin into view. */
    protected focusPin(comment: adReviewComments): void {
        if (!comment.pin) {
            return;
        }

        const index: number = this.mediaTabs.findIndex((tab) => tab.attachmentId === comment.pin!.documentId);
        if (index < 0) {
            this.snackBarService.openWarningSnackBar('The file this comment was pinned to has since been replaced.');
            return;
        }

        this.activePinId = comment.adId;
        if (index !== this.activeMedia) {
            this.selectMedia(index);
        }
        // After change detection has created or updated the viewer for that tab.
        setTimeout(() => this.pinViewer?.scrollToPin(comment.adId));
    }

    protected pinLocation(pin: AdCommentPin): string {
        return [pin.pageLabel, pin.cellRef, pin.kind === 'area' ? 'area' : null].filter(Boolean).join(' · ');
    }

    protected async setPinStatus(comment: adReviewComments, status: string): Promise<void> {
        if (!this.adDetails) {
            return;
        }

        this.setSprinner.set(true);

        let updateReviewCommentsStatus = new ReviewAdModel();
        updateReviewCommentsStatus.setCommentId(comment.adId);
        updateReviewCommentsStatus.setCommentedBy(this.sessionStorageService.getCurrentUserName());
        updateReviewCommentsStatus.setPiasRepCode(this.sessionStorageService.getCurrentRepCode());
        updateReviewCommentsStatus.setDescription(comment.text);
        updateReviewCommentsStatus.setStatus(status);

        const responseData = await this.adMarketingReviewService.updateComment(this.applicationId, updateReviewCommentsStatus)
            .then((response) => {
                return JSON.parse(response);
            })
            .catch((error) => {
                // console.error(error);
                return error;
            });

        if (responseData.status === HttpStatusCode.Ok) {
            this.snackBarService.openSuccessSnackBar('Comment Status Updated.');
            this.rebuildThread();
        } else {
            if (responseData.code === HttpStatusCode.BadRequest) {
                // Display notification on API failure.
                this.snackBarService.openFailureSnackBar("We couldn’t process your request. Please try again");
            }
            if (responseData.code === HttpStatusCode.Unauthorized) {
                this.snackBarService.openFailureSnackBar("Session timed out. Try logging in again!");
                this.router.navigate(['/login']);
            }
        }


        // await this.marketingReviewService.setCommentStatus(this.ad.id, comment.adId, status)
        //     .then((updated) => this.setAd(updated))
        //     .catch((error) => {
        //         console.error('Failed to update the comment status:', error);
        //         this.snackBarService.openFailureSnackBar('We couldn’t update that comment. Please try again');
        //     });
        this.setSprinner.set(false);
    }

    protected async postReply(comment: adReviewComments): Promise<void> {
        const text: string = (this.replyText[comment.adId] ?? '').trim();
        if (!this.adDetails || !text) {
            return;
        }

        this.setSprinner.set(true);
        let replyReviewComments = new ReviewAdModel();
        replyReviewComments.setCommentedBy(this.sessionStorageService.getCurrentUserName());
        replyReviewComments.setPiasRepCode(this.sessionStorageService.getCurrentRepCode());
        replyReviewComments.setDescription(text.toString());
        replyReviewComments.setReplayId(comment.adId);
        replyReviewComments.setReplayCommend(comment.text);
        replyReviewComments.setStatus('open');


        this.setSprinner.set(true);
        const responseData = await this.adMarketingReviewService.addComment(this.applicationId, replyReviewComments)
            .then((response) => {
                return JSON.parse(response);
            })
            .catch((error) => {
                // console.error(error);
                return error;
            });

        if (responseData.status === HttpStatusCode.Ok) {
            this.snackBarService.openSuccessSnackBar('Reply shared with the branch.');
            this.replyText[comment.adId] = '';
        } else {
            if (responseData.code === HttpStatusCode.BadRequest) {
                // Display notification on API failure.
                this.snackBarService.openFailureSnackBar("We couldn’t process your request. Please try again");
            }
            if (responseData.code === HttpStatusCode.Unauthorized) {
                this.snackBarService.openFailureSnackBar("Session timed out. Try logging in again!");
                this.router.navigate(['/login']);
            }
        }
        // await this.marketingReviewService.replyToComment(this.ad.id, comment.id, text)
        //     .then((updated) => {
        //         this.setAd(updated);
        //         this.replyText[comment.id] = '';
        //     })
        //     .catch((error) => {
        //         console.error('Failed to post the reply:', error);
        //         this.snackBarService.openFailureSnackBar('We couldn’t post that reply. Please try again');
        //     });
        this.setSprinner.set(false);
    }

    protected initialsOf(name: string): string {
        const parts: string[] = name.trim().split(/\s+/).filter(Boolean);
        if (!parts.length) {
            return '–';
        }
        const first: string = parts[0][0] ?? '';
        const last: string = parts.length > 1 ? parts[parts.length - 1][0] ?? '' : '';
        return (first + last).toUpperCase();
    }

    // ── Replace material ────────────────────────────────────────────────────────

    /**
     * A returned ad's submitter can swap the material for an amended version and resubmit.
     * Returned only — a rejection closes the submission. Submitter only, matched by name the same
     * way `canModify()` gates comments: a reviewer must not replace the branch's creative.
     */
    protected get canReplaceMaterial(): boolean {
        return !!this.adDetails && this.normalizeAdStatus(this.adDetails.status) === 'Returned' && this.adDetails.submittedBy === this.currentUserName;
    }

    protected startReplaceMaterial(): void {
        if (!this.adDetails) {
            return;
        }
        this.setReplacementFiles(this.adDetails.documents);
        this.replacingMaterial = true;
    }

    protected cancelReplaceMaterial(): void {
        this.replacingMaterial = false;
        this.replacementFiles = [];
    }

    protected removeReplacementFile(file: ReplacementFile): void {
        this.setReplacementFiles(
            this.replacementAttachments.filter((attachment) => attachment.documentId !== file.attachment.documentId)
        );
    }

    protected async onReplacementFilesSelected(event: Event): Promise<void> {
        const input = event.target as HTMLInputElement;
        const files: File[] = Array.from(input.files ?? []);
        // Reset so re-picking the same file still fires a change event.
        input.value = '';

        const { accepted, wrongType, tooLarge } = this.marketingReviewService.screenAdFiles(files);

        if (wrongType.length) {
            this.snackBarService.openWarningSnackBar(
                `${this.nameList(wrongType)} ${wrongType.length === 1 ? 'is' : 'are'} not an accepted format and was skipped.`
            );
        }
        if (tooLarge.length) {
            this.snackBarService.openWarningSnackBar(
                `${this.nameList(tooLarge)} exceeds the ${this.marketingReviewService.MAX_FILE_SIZE_MB}MB limit `
                + `(${this.marketingReviewService.MAX_VIDEO_SIZE_MB}MB for video) and was skipped.`
            );
        }
        if (!accepted.length) {
            return;
        }

        this.setSprinner.set(true);

        let marketingMaterialTypeId = this.adDetails.marketingMaterialTypeId;

        await Promise.all(accepted.map((file) => this.adMarketingReviewService.uploadAdAsset(this.applicationId.toString(), marketingMaterialTypeId.toString(), file)))
            .then((uploaded) => {
                console.log(uploaded)
                // this.setReplacementFiles([...this.replacementAttachments, ...file]);

            })
            .catch((error) => {
                console.error('Failed to upload the replacement material:', error);
                this.snackBarService.openFailureSnackBar('We couldn’t attach those files. Please try again');
            });
        this.setSprinner.set(false);
    }

    /** True once the working set differs from what the ad currently carries. */
    protected get replacementChanged(): boolean {
        if (!this.adDetails) {
            return false;
        }
        const current: string = this.adDetails.documents.map((attachment) => attachment.documentId).join(',');
        return this.replacementAttachments.map((attachment) => attachment.documentId).join(',') !== current;
    }

    protected resubmitWithNewMaterial(): void {
        if (!this.replacementFiles.length) {
            this.snackBarService.openWarningSnackBar('Attach the amended material before resubmitting.');
            return;
        }
        if (!this.replacementChanged) {
            this.snackBarService.openWarningSnackBar('Remove or add a file to replace the material.');
            return;
        }

        this.matDialog
            .open(ConfirmPopupComponent, {
                width: '440px',
                data: {
                    message: 'Resubmit with the new material?',
                    message1: 'The material you removed is withdrawn, and the advertisement goes back for review.',
                    hidecancel: false
                }
            })
            .afterClosed()
            .subscribe((result) => {
                if (result?.data) {
                    this.saveReplacement();
                }
            });
    }

    private async saveReplacement(): Promise<void> {
        if (!this.adDetails) {
            return;
        }

        this.setSprinner.set(true);
        // need to call submit Api
        // await this.adMarketingReviewService.applicationRequestChanges(this.adDetails.marketingReviewApplicationId)
        //     .then((updated) => {
        //         // this.setAd(updated);
        //         this.buildMediaTabs();
        //         this.cancelReplaceMaterial();
        //         this.snackBarService.openSuccessSnackBar('New material submitted for review.');
        //     })
        //     .catch((error) => {
        //         console.error('Failed to resubmit the advertisement:', error);
        //         this.snackBarService.openFailureSnackBar('We couldn’t process your request. Please try again');
        //     });
        this.setSprinner.set(false);
    }

    private get replacementAttachments(): documents[] {
        return this.replacementFiles.map((file) => file.attachment);
    }

    private setReplacementFiles(attachments: documents[]): void {
        this.replacementFiles = attachments.map((attachment) => ({
            attachment,
            icon: this.marketingReviewService.iconOf(attachment.documentName),
            tone: this.marketingReviewService.toneOf(attachment.documentName),
            // meta: [
            //     this.marketingReviewService.formatLabelOf(attachment),
            //     this.marketingReviewService.fileSizeLabel(attachment.fileSizeKb)
            // ].join(' · ')
        }));
    }

    private nameList(files: File[]): string {
        return files.map((file) => file.name).join(', ');
    }

    // ── Decisions ───────────────────────────────────────────────────────────────

    protected approve(): void {
        this.confirmAndSubmit(
            'Approved',
            'Approve this advertisement?',
            'The branch will be notified that it is cleared for publication.'
        );
    }

    protected returnForChanges(): void {
        this.confirmAndSubmit(
            'Returned',
            'Return this advertisement for changes?',
            'The branch will see your comments and can resubmit an amended version.'
        );
    }

    protected reject(): void {
        this.confirmAndSubmit(
            'Rejected',
            'Reject this advertisement?',
            'This closes the submission. The branch will need to raise a new advertisement.'
        );
    }

    private confirmAndSubmit(decision: ReviewDecision, message: string, message1: string): void {
        this.commentsTouched = true;

        /*
         * A decision still has to be explained, but the explanation can already be in the thread:
         * a reviewer who has posted their concerns as comments should not have to retype them into
         * the composer to press Approve.
         */
        if (!this.overallComments.trim() && !this.threadCount) {
            this.snackBarService.openWarningSnackBar('Please share your comments before recording a decision.');
            return;
        }

        this.matDialog
            .open(ConfirmPopupComponent, {
                width: '440px',
                data: { message, message1, hidecancel: false }
            })
            .afterClosed()
            .subscribe((result) => {
                if (result?.data) {
                    this.submitReview(decision);
                }
            });
    }

    private async submitReview(decision: ReviewDecision): Promise<void> {
        if (!this.adDetails) {
            return;
        }

        // const review: Omit<AdReview, 'id'> = {
        //     reviewerName: this.sessionStorageService.getCurrentUserPersonName() || 'Reviewer',
        //     reviewerRole: this.sessionStorageService.getCurrentUserRole() || 'Reviewer',
        //     reviewedOn: new Date().toISOString(),
        //     decision,
        //     overallComments: this.overallComments.trim(),
        //     // No checklist on this page — the decision is carried entirely by the comments.
        //     criteria: []
        // };

        this.setSprinner.set(true);
        let request;
        if (decision == 'Approved') {
            if (this.sessionStorageService.getCurrentUserRole() == 'Marketing Review') {
                request = this.adMarketingReviewService.marketingTeamApprove(this.applicationId);
            } else {
                request = this.adMarketingReviewService.complianceApprove(this.applicationId);
            }
        } else if (decision == 'Returned') {
            request = this.adMarketingReviewService.applicationRequestChanges(this.applicationId)
        } else {
            request = this.adMarketingReviewService.applicationReject(this.applicationId)
        }

        const responseData = await request
            .then((response) => {
                return JSON.parse(response);
            })
            .catch((error) => {
                // console.error(error);
                return error;
            });

        if (responseData.status === HttpStatusCode.Ok) {
            this.snackBarService.openSuccessSnackBar(`Review recorded — advertisement ${decision.toLowerCase()}.`);
            this.router.navigate(['/marketing-review']);
        } else {
            if (responseData.code === HttpStatusCode.BadRequest) {
                // Display notification on API failure.
                this.snackBarService.openFailureSnackBar("We couldn’t process your request. Please try again");
            }
            if (responseData.code === HttpStatusCode.Unauthorized) {
                this.snackBarService.openFailureSnackBar("Session timed out. Try logging in again!");
                this.router.navigate(['/login']);
            }
        }
        // await this.marketingReviewService.saveReview(this.ad.id, review)
        //     .then((updated) => {
        //         this.setAd(updated);
        //         this.overallComments = '';
        //         this.commentsTouched = false;
        //         this.snackBarService.openSuccessSnackBar(`Review recorded — advertisement ${decision.toLowerCase()}.`);
        //         this.router.navigate(['/marketing-review/dashboard']);
        //     })
        //     .catch((error) => {
        //         console.error('Failed to record the review:', error);
        //         this.snackBarService.openFailureSnackBar('We couldn’t process your request. Please try again');
        //     });
        this.setSprinner.set(false);
    }

    protected goBack(): void {
        this.router.navigate(['/marketing-review']);
    }

    private normalizeAdStatus(value: string): AdStatus {
        const normalized = (value || '')
            .trim()
            .toLowerCase()
            .replace(/[\s-]+/g, '_');

        switch (normalized) {
            case 'draft':
                return 'Draft';

            case 'submitted':
            case 'pending':
            case 'pending_review':
                return 'Submitted';

            case 'in_review':
            case 'under_review':
                return 'In_Review';

            case 'approved':
                return 'Approved';

            case 'returned':
            case 'changesrequested':
                return 'Returned';

            case 'rejected':
                return 'Rejected';

            default:
                // Verify this fallback against the backend's actual status values.
                return 'Submitted';
        }
    }
}
